import moment from 'moment';
import { patientMaritalStatusOptions, newPatientObj, genderOptions, australianStateOptions } from './AnswerArray';
import { PATIENT, PRACTITIONER } from '../config-client';

/*
* Function purpose: calculate the BMI given their height and weight
* Parameter purpose:
* - height: used in the formula to calculate BMI
* - weight: used in the formula to calculate BMI
* - Example: BMI(167, 46)
*/
export const BMI = function (height, weight) {
  if (height === 0) return 0;
  const m = parseFloat(height / 100);
  const BMI = weight / (m * m);
  return BMI.toFixed(1);
};

/*
* Function purpose: calculate the age given their date of birth
* Parameter purpose:
* - birthDateString: the stringified version of the date to be formattd
* - format: used in the formula to calculate BMI
* - Example: age("1993-09-17", "DD-MM-YYYY")
*/
export const age = function (birthDateString, format) {
  const now = moment();
  const birthDate = moment(birthDateString, format);
  const yearDiff = moment.duration(now - birthDate).as('years');
  return Math.floor(yearDiff);
};

/*
* Function purpose: compile the form object for the questionnaire response resource object
* Parameter purpose:
* - serverQnaire: the questionnaire response object
* - Example: ~Print questionnaire.response in QuestionnairePage.js to see example~
*/
export const qnaireDTOIncoming = (serverQnaire) => {
  // loop through each item in the questionnaire response resource
  // the outer loop goes through each questionnaire page or group id
  for (let i = 0; i < serverQnaire.item.length; i++) {
    // loop through each item in the question group
    // the inner loop goes through each question in th question group
    for (let j = 0; j < serverQnaire.item[i].item.length; j++) {
      // questionItem is the object of one question
      const questionItem = serverQnaire.item[i].item[j];
      // check if this question is an option item
      // this could either be a single select option, or multi select option
      if (questionItem.answerOption !== null && questionItem.answerOption !== undefined) {
        // go through each option that that user last saved
        for (let k = 0; k < questionItem.answerOption.length; k++) {
          var optionObj = questionItem.answerOption[k];
          // extract value ad change the key name to "display"
          // this is because it's easier to map to the form object with the key name "display"
          if (optionObj.valueString !== undefined) {
            Object.defineProperty(optionObj, 'display', Object.getOwnPropertyDescriptor(optionObj, 'valueString'));
            delete optionObj.valueString;
          }
        }

        // change the key name to "options"
        // this is because it's easier to map to the form object with the key name "options"
        Object.defineProperty(questionItem, 'options', Object.getOwnPropertyDescriptor(questionItem, 'answerOption'));
        delete questionItem.answerOption;
        // the object key could be answerOption or answerOptions, it could even be option or options
        // FHIR for Azure API is still in beta and Hl7 FHIR also keeps changing the object key names
        // so if this occurs make sure to change the checks in this function
      } else if (questionItem.answerOptions !== null && questionItem.answerOptions !== undefined) {
        // does the same as the if condition above but with answerOptions key instead of answerOption
        for (let k = 0; k < questionItem.answerOptions.length; k++) {
          var optionObject = questionItem.answerOptions[k];
          if (optionObject.valueString !== undefined) {
            Object.defineProperty(optionObject, 'display', Object.getOwnPropertyDescriptor(optionObject, 'valueString'));
            delete optionObject.valueString;
          }
        }
        // Object.defineProperty(questionItem, "options",
        //     Object.getOwnPropertyDescriptor(questionItem, "options"));
        // delete questionItem["options"];
      }
    }
  }
  return serverQnaire;
};

// from https://stackoverflow.com/questions/8431651/getting-a-diff-of-two-json-objects/8432188
export function jsonDiff(obj1, obj2) {
  var result = {};
  if (Object.is(obj1, obj2)) {
       return undefined;
  }
  if (!obj2 || typeof obj2 !== 'object') {
      return obj2;
  }
  Object.keys(obj1 || {}).concat(Object.keys(obj2 || {})).forEach(key => {
      if (typeof obj2[key] === 'number') {
        obj2[key] = obj2[key].toString();
      }
      if (typeof obj1[key] === 'number') {
        obj1[key] = obj1[key].toString();
      }
      if(obj2[key] !== obj1[key] && !Object.is(obj1[key], obj2[key])) {
          result[key] = obj2[key];
      }
      if(typeof obj2[key] === 'object' && typeof obj1[key] === 'object') {
          const value = jsonDiff(obj1[key], obj2[key]);
          if (value !== undefined) {
            result[key] = value;
          }
      }
  });
  result = removeEmptyDiff(result);
  return result;
}

export function removeEmptyDiff(obj) {
  var result = [];
  for (var key in obj) {
    if (obj[key] !== undefined && Object.keys(obj[key]).length !== 0) {
      result.push(key);
    }
  }
  return result;
}

// Use: Generate a list of question IDs of questions attached to recommendations that have been triggered with the correct answers
// changedQuestions = array of qIDs (of the form "[A-W][0-9]+")
// parsedResponse = JSON object of qIDs mapped to the user's actual answers | { qID: Answer, qID: Answer... }
// recommendations = JSON object of Recommendations formatted as is currently the case in Recommendations.js
export function recommendationQuestionsToUpdate(changedQuestions, parsedResponse, recommendations) {
  var questions = {};
  for (const qID of changedQuestions) {
    const section = qID[0];
    const qNum = qID.substring(1);
    // Check if question has been answered correctly
    if (recommendations[section][qNum] !== undefined && JSON.stringify(recommendations[section][qNum].answer) === JSON.stringify(parsedResponse[qID])) {
      questions[qID] = recommendations[section][qNum].recommendation;
    }
  }
  // Returns JSON object of this form: {
  //   A1: "Recommendation"
  //   N66: "Recommendation"
  // }
  return questions;
}

// Convert letter to number: A -> 0 ... W -> 22
export function convertLetterToNumber (string) {
  return string.charCodeAt(0) - 65;
}

/*
* Function purpose: from the form object, regenerate or generate a new response resource object to PUT/POST to the backend
* Parameter purpose:
* - dataObject: the questionnaire response is used to map the form object to the correct object key names, if the response doesn't exist especially
* - hasResponse: if the response already exists in the server then this is true
* - formAnswers: the form object of the response currently
* - source: the patient response which will be changed
* - user: the user/clincian resource to extract some important values to construct the response resource object
* - responseId: if this is "DTT" then the resource id mapping is skipped since the POST request won't accept a resource with an id
*/
export const generateResponseData = (dataObject, hasResponse, formAnswers, source, user, responseId) => {
  // initialize the output object
  const outputObject = {
    resourceType: 'QuestionnaireResponse',
    status: dataObject.status,
  };

  // add author, source and status
  let status = 'in-progress';
  // check if the response is a new response or the response resource object already exists and map values accordingly

  if (hasResponse) {
    outputObject.author = {};
    outputObject.author.display = user.name[0].given[0];
    outputObject.author.reference = `${PRACTITIONER}/${(user.id).toString()}`;
    outputObject.source = {};
    outputObject.source.display = source.resource.name[0].given[0];
    outputObject.source.reference = `${PATIENT}/${(source.resource.id).toString()}`;
    // id will be either "DTT-<Patient-ID>"" or "Recommendation-<Patient-ID>"
    outputObject.id = `${responseId}-${(source.resource.id).toString()}`;
  }

  outputObject.item = [];
  // the two objects below are used as skeletons to be clones and used to create a new question/group object
  const innerObject = {
    linkId: '-',
    definition: '',
    text: '-',
    item: [],
  };

  const questionObject = {
    linkId: '-',
    text: '-',
    answer: [],
  };

  // this loop creates the group objects by using data.js object values
  const groupArray = [];
  for (let i = 0; i < dataObject.item.length; i++) {
    // clones skeleton object without reference
    const currGroup = JSON.parse(JSON.stringify(innerObject));

    currGroup.linkId = dataObject.item[i].linkId;
    currGroup.text = dataObject.item[i].text;
    const questionArray = [];
    // this loop creates the question object
    for (let j = 0; j < dataObject.item[i].item.length; j++) {

      const currQuestion = JSON.parse(JSON.stringify(questionObject));

      const tempQObj = JSON.parse(JSON.stringify(questionObject));
      tempQObj.linkId = dataObject.item[i].item[j].linkId;
      currQuestion.linkId = dataObject.item[i].item[j].linkId;
      // currQuestion.answer = [];

      currQuestion.text = dataObject.item[i].item[j].text;
      // getValueKey returns: {valueString: ""}/{valueInteger: ""}/etc
      currQuestion.answer[0] = getValueKey(dataObject.item[i].item[j].type, '');

      // combine the group id and link id to create the key naming convention used in the form object
      const linkIds = currGroup.linkId + currQuestion.linkId;

      if (hasResponse && linkIds in formAnswers) {
        let responseVal = formAnswers[linkIds];
        if (formAnswers[linkIds] instanceof Array) {
          // this is a multi select option so go through each option to extract the value
          for (let k = 0; k < formAnswers[linkIds].length; k++) {
            responseVal = formAnswers[linkIds][k].display;
            currQuestion.answer.push(getValueKey(dataObject.item[i].item[j].type, responseVal.toString()));
          }

          responseVal = responseVal[0].display;
        } else if (formAnswers[linkIds] instanceof Object) {
          // this is a single select option, so extract the one value
          responseVal = responseVal.display;
          currQuestion.answer[0] = getValueKey(dataObject.item[i].item[j].type, responseVal.toString());
        } else {
          // this is a text field value so you can get the data as shown below
          currQuestion.answer[0] = getValueKey(dataObject.item[i].item[j].type, responseVal.toString());
        }
      } else {
        // make this field empty or unselected because the user has not filled in the asnwer for this question
        currQuestion.answer[0] = getValueKey(dataObject.item[i].item[j].type, '');
      }

      // append question object to temporary var for item: [] in group object
      questionArray.push(currQuestion);
    }
    currGroup.item = questionArray;
    // append group object to temporary var for item: []
    groupArray.push(currGroup);
  }

  outputObject.status = status;
  outputObject.item = groupArray;
  return outputObject;
};

/*
* Function purpose: depending on the response values data type, create the data type key name that the azure API accepts
* Parameter purpose:
* - dataKey: the current key name which has the data type name in it
* - val: the value stored in that key
*/
function getValueKey(dataKey, val) {
  // check for the data type and return the value stored in a new object with the new key name
  if (dataKey === 'integer') {
    return { valueInteger: val };
  } else if (dataKey === 'date') {
    if (val !== '') {
      // some dates come in the wrong format, so this is using the moment library to standardise date formats
      return { valueDate: moment(val, 'DD-MM-YYYY').format('YYYY-MM-DD') };
    }
    return { valueDate: '' };
  }
  return { valueString: val };
}

/*
* Function purpose: convert a response resource from the azure server to a form object to repopulate the form fields with existing data
* Parameter purpose:
* - obj: this is the patient resource object that needs to be mapped to the form object for repopulation
* - data: the questionnaire response is necessary to know certain namesof keys as Azure is in Beta and keeps changing some key names
* - isPatientVersion: for when the response is new
*/
export const parseResponse = (obj, data, isPatientVersion) => {
  const formObject = {};
  for (let i = 0; i < obj.item.length; i++) {
    const groupId = obj.item[i].linkId;
    for (let j = 0; j < obj.item[i].item.length; j++) {
      var q = obj.item[i].item[j];
      if (q.answer === undefined) {
        continue;
      }
      const qId = q.linkId;
      
      let val = getInverseValueKey(q.answer[0]);

      if (val === '') {
        if (q.answer[0] !== undefined && q.answer[0].valueString === "" && q.answer[1] !== undefined) {
          q.answer.shift();
        }
        else {
          continue;
        }
      }
      const groupIndex = searchIndex(groupId, data.item, 'linkId');
      const qIndex = searchIndex(qId, data.item[groupIndex].item, 'linkId');

      const optionItem = checkForOptionType(data.item[groupIndex].item[qIndex]);
      val = getChoiceType(groupIndex, qIndex, i, j, obj, data, optionItem);

      if (val === '') {
        continue;
      }
      const key = groupId + qId;
      formObject[key] = val;
    }
  }
  return formObject;
};

/*
* Function purpose: The azure API for FHIR keeps changing this key name (for some reason, probably because it's in beta) ...
*                   so I made this function to make it easier to return the option key's name
* Parameter purpose:
* - dataItem: the object which represent a question item
*/
function checkForOptionType(dataItem) {
  if (typeof dataItem.options !== 'undefined') {
    return 'options';
  } else if (typeof dataItem.option !== 'undefined') {
    return 'option';
  } else if (typeof dataItem.answerOption !== 'undefined') {
    return 'answerOption';
  }
  return 'answerOptions';
}

/*
* Function purpose: Search for a value in an object which is in an array with multiple objects and return that object
* Parameter purpose:
* - nameKey: the value that we are looking for
* - myArray: the array to travserse through
* - key: the key that the value is stored in for the objects
*/
export const search = (nameKey, myArray, key) => {
  for (let i = 0; i < myArray.length; i++) {
    if (myArray[i][key].toUpperCase() === nameKey.toUpperCase()) {
      return myArray[i];
    }
  }
};

/*
* Function purpose: Search for a value in an object which is in an array with multiple objects and return the index of that object
* Parameter purpose:
* - nameKey: the value that we are looking for
* - myArray: the array to travserse through
* - key: the key that the value is stored in for the objects
*/
function searchIndex(nameKey, myArray, key) {
  for (let i = 0; i < myArray.length; i++) {
    if (myArray[i][key].toUpperCase() === nameKey.toUpperCase()) {
      return i;
    }
  }
}

/*
* Function purpose: return the choice type in the form object format
* Parameter purpose:
* - groupIndex: the index that represents the question group we are traversing in the questionnare resource
* - qIndex: the index that represeents the question item we are examining in the questionnare resource
* - i: the index that represents the question group we are traversing in the questionnare RESPONSE resource
* - j: the index that represeents the question item we are examining in the questionnare RESPONSE resource
* - obj: the questionnaire response resource
* - data: the questionnaire resource
* - option: the key name of the option key as azure keeps changing this key name
*/
function getChoiceType(groupIndex, qIndex, i, j, obj, data, option) {
  const lst = [];
  let innerObj = {};
  let val = '';
  var tempId = '';
  var tempObj = {};
  // make sure the option key exists
  if (data.item[groupIndex].item[qIndex][option] !== undefined &&
    typeof data.item[groupIndex].item[qIndex][option] !== 'undefined') {
    // check if this is a multi-select option by checking if the object has a "repeats" key
    if (data.item[groupIndex].item[qIndex].repeats !== undefined) {
      // traverse through each value to extract the values and convert it into form object format
      // this returns an array of selections
      for (let k = 0; k < obj.item[i].item[j].answer.length; k++) {
        val = '';
        tempObj = {};
        tempId = '';
        val = getInverseValueKey(obj.item[i].item[j].answer[k]);

        tempId = getChoiceId(data.item[groupIndex].item[qIndex], val, option);

        tempObj = {
          id: tempId,
          display: val,
        };
        lst.push(tempObj);
      }
      return lst;
    }
    // the option is a single select option, so extract the value and store it in the form object format
    // this returns an object with the value and id of the selection
    tempId = '';
    val = getInverseValueKey(obj.item[i].item[j].answer[0]);
    tempId = getChoiceId(data.item[groupIndex].item[qIndex], val, option);

    innerObj = {
      id: tempId,
      display: val,
    };
    return innerObj;

    // DEPRECATED, I believe: remove and test
  } else if (data.item[groupIndex].item[qIndex][option] !== undefined) {
    if (data.item[groupIndex].item[qIndex].repeats !== undefined) {
      for (let k = 0; k < obj.item[i].item[j].answer.length; k++) {
        val = '';
        tempObj = {};
        tempId = '';
        val = getInverseValueKey(obj.item[i].item[j].answer[k]);

        tempId = getChoiceId(data.item[groupIndex].item[qIndex], val, option);

        tempObj = {
          id: tempId,
          display: val,
        };
        lst.push(tempObj);
      }
      return lst;
    }
    tempId = '';
    val = getInverseValueKey(obj.item[i].item[j].answer[0]);
    tempId = getChoiceId(data.item[groupIndex].item[qIndex], val, option);

    innerObj = {
      id: tempId,
      display: val,
    };
    return innerObj;
  }
  // the value is not an option field, it's a free form text field, so extract and return that value
  val = getInverseValueKey(obj.item[i].item[j].answer[0]);

  return val;
}

/*
* Function purpose: get the id of each choice as it is needed when repopulating the form in the correct format
* Parameter purpose:
* - data: the questionnaire resource
* - val: the value that we are looking for
* - option: the key name of the option key as azure keeps changing this key name
*/
function getChoiceId(data, val, option) {
  let tempId = '';
  // check if option key name is undefined
  if (data[option] !== undefined) {
    // loop through each value in the possible options
    for (let l = 0; l < data[option].length; l++) {
      // check if this option is the one we are searching for
      if (getInverseValueKey(data[option][l]) === val) {
        // extract value
        tempId = data[option][l].id;
      }
      // it could either come in the above format or the format below so check for both
      if (data[option][l].display === val) {
        // extract value
        tempId = data[option][l].id;
      }
    }
    // DEPRECATED: remove and test
  } else {
    for (let l = 0; l < data[option].length; l++) {
      if (getInverseValueKey(data[option][l]) === val) {
        tempId = data[option][l].id;
      }
      if (data[option][l].display === val) {
        tempId = data[option][l].id;
      }
    }
  }

  return tempId;
}

/*
* Function purpose: used to convert from resource from API to form object, this is to extract the value
* Parameter purpose:
* - answer: the array of objects with the values
*/
function getInverseValueKey(answer) {
  let val = '';
  if (answer.valueString !== undefined) {
    val = answer.valueString;
  } else if (answer.valueDate !== undefined) {
    val = answer.valueDate;
    val = moment(val, 'YYYY-MM-DD').format('DD-MM-YYYY');
  } else if (answer.valueInteger !== undefined) {
    val = answer.valueInteger;
  }
  return val;
}


/*
* Function purpose: convert form object to edit or create new patient resource
* Parameter purpose:
* - patientObj: the patient resource object (is null when a new patient resource is to be created)
* - formObj: the form object that needs to be converted to a patient resource object
* - isNewPatient: For when the patient is a new patient not existing in the server already
*/
export const editPatientDetails = (patientObj, formObj, isNewPatient) => {
  let tempObj = JSON.parse(JSON.stringify(patientObj));
  // if a new patient, use newPatientObj which is a skeleton of the patient resource
  if (isNewPatient) tempObj = JSON.parse(JSON.stringify(newPatientObj));

  // loop through the form object to extract values
  for (const ind in formObj) {
    if (formObj.hasOwnProperty(ind)) {
      if (ind === 'given') {
        // assign the values from the fromobject to patient resource object
        tempObj.name[0].given = [formObj[ind]];
      } else if (ind === 'family') {
        tempObj.name[0].family = formObj[ind];
      } else if (ind === 'gender') {
        tempObj.gender = formObj[ind].display.toLowerCase();
      } else if (ind === 'birthDate') {
        tempObj.birthDate = formObj[ind];
      } else if (ind === 'pAddressLine1') {
        tempObj.address[0].line[0] = formObj[ind];
        tempObj.address[0].line[1] = '';
      } else if (ind === 'pAddressLine2') {
        tempObj.address[0].line.push(formObj[ind]);
      } else if (ind === 'pAddressCity') {
        tempObj.address[0].city = formObj[ind];
      } else if (ind === 'pAddressState') {
        tempObj.address[0].state = formObj[ind].display;
      } else if (ind === 'pAddressPC') {
        tempObj.address[0].postalCode = formObj[ind];
      } else if (ind === 'pEmail' || ind === 'pHome' || ind === 'pMobile') {
        tempObj.telecom = findContactSystem(tempObj.telecom, formObj[ind], ind.substring(1).toLowerCase());
      } else if (ind === 'maritalStatus') {
        const tempVar = JSON.parse(JSON.stringify(formObj[ind]));
        delete tempVar.id;
        tempObj.maritalStatus.coding = [tempVar];
      } else if (ind === 'generalPractitioner') {
        tempObj.generalPractitioner[0].display = formObj[ind];
      } else if (ind === 'cRelationship') {
        // Uncomment these lines if we ever change how we integrate relationship with the form
        // tempObj.contact[0].relationship[0].coding[0].code = (search(formObj[ind], contactRelationshipOptions, 'display')).code;
        // tempObj.contact[0].relationship[0].coding[0].system = 'http://terminology.hl7.org/CodeSystem/v2-0131';
        // Test to check whether the relationship object is in the correct format & edit if need be
        if (tempObj.contact[0].relationship[0] !== null && tempObj.contact[0].relationship[0] !== undefined) {
          tempObj.contact[0].relationship[0].text = formObj[ind];
        }
        else
        {
          tempObj.contact[0].relationship.text = formObj[ind];
        }
      } else if (ind === 'cGiven') {
        tempObj.contact[0].name.given[0] = formObj[ind];
      } else if (ind === 'cFamily') {
        tempObj.contact[0].name.family = formObj[ind];
      } else if (ind === 'cEmail' || ind === 'cHome' || ind === 'cMobile') {
        tempObj.contact[0].telecom = findContactSystem(tempObj.contact[0].telecom, formObj[ind], ind.substring(1).toLowerCase());
      } else if (ind === 'cGender') {
        tempObj.contact[0].gender = formObj[ind].display.toLowerCase();
      }
    }
  }
  return tempObj;
};

/*
* Function purpose: convert patient resource object to the form object
* Parameter purpose:
* - patientObj: the patient resource object coming from the server
*/
export const formatPatientDetails = (patientObj) => {
  const tempObj = {
    given: '',
    family: '',
    gender: '',
    birthDate: '',
    pEmail: '',
    pMobile: '',
    pHome: '',
    maritalStatus: '',
    pAddressLine1: '',
    pAddressLine2: '',
    pAddressCity: '',
    pAddressState: '',
    pAddressPC: '',
    cGiven: '',
    cFamily: '',
    cGender: '',
    cMobile: '',
    cRelationship: '',
    generalPractitioner: '',
  };

  // assign the values from the patient resource object
  tempObj.given = patientObj.name[0].given[0];
  tempObj.family = patientObj.name[0].family;
  // look for the "display" key and its value in the patient resource object
  let tempGender = search(patientObj.gender, genderOptions, 'display');

  // this is what a selection or one choice option looks like in the form object
  tempObj.gender = {
    id: tempGender.id,
    display: tempGender.display,
  };
  // look for the email system
  tempGender = search('email', patientObj.telecom, 'system');


  tempObj.pEmail = tempGender.value;
  tempObj.birthDate = patientObj.birthDate;

  tempObj.pAddressLine1 = patientObj.address[0].line[0];
  // check if the user has entered a second line for their address
  if (patientObj.address[0].line.length === 2) {
    tempObj.pAddressLine2 = patientObj.address[0].line[1];
  }
  tempObj.pAddressCity = patientObj.address[0].city;
  tempObj.pAddressState = search(patientObj.address[0].state, australianStateOptions, 'display');

  tempObj.pAddressPC = patientObj.address[0].postalCode;

  tempObj.cGiven = patientObj.contact[0].name.given[0];
  tempObj.cFamily = patientObj.contact[0].name.family;
  tempGender = search(patientObj.contact[0].gender, genderOptions, 'display');
  tempObj.cGender = {
    id: tempGender.id,
    display: tempGender.display,
  };

  // search by two keys and their values since phone, mobile and email can be mixed up by using only one key
  let tempMobile = searchByTwoKeys('phone', 'mobile', patientObj.contact[0].telecom, 'system', 'use');
  tempObj.cMobile = tempMobile.value;

  tempObj.cRelationship = patientObj.contact[0].relationship[0].text;

  tempMobile = searchByTwoKeys('phone', 'mobile', patientObj.telecom, 'system', 'use');
  tempObj.pMobile = tempMobile.value;

  const tempHome = searchByTwoKeys('phone', 'home', patientObj.telecom, 'system', 'use');
  tempObj.pHome = tempHome.value;

  const tempMS = search(patientObj.maritalStatus.coding[0].display, patientMaritalStatusOptions, 'display');
  tempObj.maritalStatus = tempMS;

  tempObj.generalPractitioner = patientObj.generalPractitioner[0].display;
  return tempObj;
};

/*
* Function purpose: find the value of the contact system by system and use key values
* Parameter purpose:
* - telecom: the array of email, phone and mobile
* - form: form value that is being searched for
* - key: the type of system of the telecom (i.e. email, phone, home phone)
*/
// TODO - Fix this function up :)
function findContactSystem(telecom, form, key) {
  let exists = false;
  // go through each item (email, phone, homephone)
  for (let i = 0; i < telecom.length; i++) {
    // assign a default value for the use and system
    var tempKey = 'email';
    var tempUse = 'home';

    if (key === 'mobile') {
      tempKey = 'phone';
      tempUse = 'mobile';
    } else if (key === 'home') {
      tempKey = 'phone';
    }
    // check which system and use the value is supposed to be
    if (telecom[i].system === tempKey && telecom[i].use === tempUse) {
      telecom[i].value = form;
      exists = true;
      break;
    }
  }

  // if such an object doesn't exist in the telecom, create it
  if (!exists) {
    const temp = {};

    var tempKeyNew = 'email';
    var tempUseNew = 'home';
    if (key === 'mobile') {
      tempKeyNew = 'phone';
      tempUseNew = 'mobile';
    } else if (key === 'home') {
      tempKeyNew = 'phone';
    }

    temp.system = tempKeyNew;
    temp.value = form;
    temp.use = tempUseNew;
    telecom.push(temp);
  }
  return telecom;
}

/*
* Function purpose: Search for a value in an object which is in an array with multiple objects and return that object
* Parameter purpose:
* - firstVal: the first value we are looking for
* - secondVal: the second value we are looking for
* - myArray: the array to travserse through
* - firstKey: the first key that the value is stored in for the objects
* - secondKey: the first key that the value is stored in for the objects
*/
export function searchByTwoKeys(firstVal, secondVal, myArray, firstKey, secondKey) {
  for (let i = 0; i < myArray.length; i++) {
    if (myArray[i][firstKey].toUpperCase() === firstVal.toUpperCase() && myArray[i][secondKey].toUpperCase() === secondVal.toUpperCase()) {
      return myArray[i];
    }
  }
}

/*
* Function purpose: check if a certain object has a certain key
* Parameter purpose:
* - obj: the parent object to search through
* - key: the key we are looking for
*/
export const hasKeys = (obj, key) => key.split('.').every((x) => {
  if (typeof obj !== 'object' || obj === null || !(x in obj) || typeof obj[x] === 'undefined') { return false; }
  obj = obj[x];
  return true;
});

/*
* Function purpose: convert recommendations form object to the resource object
* Parameter purpose:
* - text: the actual recommendation details
* - type: the type of recommendation (either "detailed or followUp")
* - patient: the subject of the observation or recommendations
* - practitioner: the performer of the observation or the recommendation
* - resource: the existing recommendations resource, this is null the recommendations are brand new
*/
export const recFormToResource = (text, type, patient, practitioner, resource) => {
  // this is a skeleton resource object to fill out
  const emptyObject = {
    resourceType: 'Observation',
    status: 'final',
    code: {
      text: 'Follow-Up',
    },
    subject: {
      reference: 'Patient/123',
    },
    performer: [{
      reference: 'Practioner/456',
    }],
    valueString: '',
  };

  // if the resource exist, just change the text or valueString
  if (resource !== null) {
    const temp = JSON.parse(JSON.stringify(resource));
    temp.valueString = text;
    return temp;

    // if the resource doesn't exist, then assign each value with the passed in parameters
  }
  emptyObject.subject.reference = `Patient/${patient}`;
  emptyObject.performer[0].reference = `Practitioner/${practitioner}`;
  emptyObject.code.text = type;
  emptyObject.valueString = text;
  return emptyObject;
};
/*
* Function purpose: convert recommendation resource object to form object
* Parameter purpose:
* - followUp: the follow up recommendations type text
* - detailed: the detailed recommendations type text
*/

export const recResourceToForm = (followUp, detailed) => {
  const initialValues = {
    followUp,
    detailed,
  };

  return initialValues;
};



// WEBPACK FOOTER //
// ./src/components/simpleFunctions.js